import dayjs from 'dayjs';
import { DateFormat } from '/@/modules/core/app/constants/date.constants';
import { BaseModel } from '@/modules/core/app/models/abstracts/BaseModel';
import { ModelRelationshipFactory } from '/@/modules/core/app/models/relationships/ModelRelationshipFactory';
import { EntityPolicyResult } from '@/modules/core/app/models/policies/EntityPolicyResult';
import { timeAgo } from '/@/modules/core/app/helpers/DateHelper';
import { ModelType } from '@/modules/core/app/constants/ModelType';
import { v4 as uuidv4 } from 'uuid';

export class BaseEntityModel extends BaseModel {
  public tempId: string;

  public createdAt: string;

  public updatedAt: string;

  public deletedAt: string;

  public policies: EntityPolicyResult;

  public rowCount?: number | undefined;

  constructor(model: Partial<BaseEntityModel>) {
    super(model);

    this.tempId = model.tempId ?? uuidv4();
    this.createdAt = model.createdAt as string;
    this.updatedAt = model.updatedAt as string;
    this.deletedAt = model.deletedAt as string;
    this.policies = new EntityPolicyResult(model.policies);
    this.rowCount = model.rowCount ?? undefined;
  }

  /**
   * only use ModelType- string only on this definition for legacy purposes
   */
  getModelName(): ModelType | string {
    return ModelType.GENERIC;
  }

  static mapModelWithoutRelationships<T extends BaseEntityModel>(
    this: typeof BaseModel & (new (model?: Partial<T>) => T),
    model?: Partial<T>
  ): T {
    const entity: T = new this(model ?? {}) as T;
    const relationships: Partial<T> = ModelRelationshipFactory.build(entity.getModelName(), {});
    Object.entries(relationships).forEach((parts: [string, unknown]) => {
      const key: string = parts[0];
      // eslint-disable-next-line no-prototype-builtins
      if (entity.hasOwnProperty(key)) {
        // @ts-ignore
        delete entity[key];
      }
    });

    return entity;
  }

  /**
   * @deprecated
   *
   * @param instance
   * @param model
   * @param modelName
   */
  setRelationships(instance: BaseModel, model: BaseModel, modelName: ModelType): void {
    Object.assign(instance, ModelRelationshipFactory.build(modelName, model));
  }

  get formattedCreatedAt(): string {
    return dayjs(this.createdAt).format(DateFormat.MONTH_DAY_YEAR);
  }

  get formattedUpdatedAt(): string {
    return dayjs(this.updatedAt).format(DateFormat.MONTH_DAY_YEAR);
  }

  get timeAgoCreatedAt(): string {
    return timeAgo(this.createdAt);
  }

  get timeAgoUpdatedAt(): string {
    return timeAgo(this.updatedAt);
  }

  get timeAgoDeletedAt(): string {
    return timeAgo(this.deletedAt);
  }

  get isoFormattedCreatedAt(): string {
    return dayjs(this.createdAt).format(DateFormat.ISO);
  }

  get isoFormattedUpdatedAt(): string {
    return dayjs(this.updatedAt).format(DateFormat.ISO);
  }

  get isoFormattedDeletedAt(): string {
    return dayjs(this.deletedAt).format(DateFormat.ISO);
  }

  get isoFormattedCreatedAtTime(): string {
    return dayjs(this.createdAt).format(DateFormat.ISO_TIME_WITH_TIMEZONE);
  }

  get isoFormattedUpdatedAtTime(): string {
    return dayjs(this.updatedAt).format(DateFormat.ISO_TIME_WITH_TIMEZONE);
  }

  clone(): this {
    return this.constructor.mapModel(JSON.parse(JSON.stringify(this)));
  }
}
